import { z } from "zod";
export const WORKSPACE_KINDS = ["board_pack", "engagement", "general"];
export const WORKSPACE_DOC_STATUSES = ["draft", "in_review", "final"];
// Per-document storage backend (ADR-0059). "blob" is the default working-layer
// store; "sharepoint" routes the document to SharePoint for real Office
// co-authoring. The whole SharePoint path is dormant until SHAREPOINT_SITE_PATH
// is set — the service rejects a "sharepoint" create when it is unset.
export const WORKSPACE_STORAGE_LOCATIONS = ["blob", "sharepoint"];
export const WorkspaceCreateSchema = z.object({
    name: z.string().min(1).max(200),
    kind: z.enum(WORKSPACE_KINDS).default("general"),
    linkedEntityType: z.string().max(40).optional(),
    linkedEntityId: z.string().uuid().optional(),
    visibleToRoles: z.array(z.string().max(40)).max(20).optional(),
    /**
     * Personal vault owner (ADR-0106). Present ⇒ the workspace is visible only
     * to this address and to admins; `visibleToRoles` is then ignored. Only an
     * admin may name someone other than themselves.
     */
    ownerEmail: z.string().email().max(320).nullable().optional(),
});
/** Admin archive/restore of a workspace. */
export const WorkspaceArchiveSchema = z.object({
    archived: z.boolean(),
});
/** Admin listing may include archived rows; everyone else never sees them. */
export const WorkspaceListQuerySchema = z.object({
    includeArchived: z
        .union([z.literal("true"), z.literal("false")])
        .optional()
        .transform((v) => v === "true"),
});
/**
 * Working-document list filters (admin-only opts; service ANDs with isAdmin()).
 * Mirrors vault document list archive semantics.
 */
export const WorkspaceDocumentListQuerySchema = z.object({
    includeArchived: z.coerce.boolean().optional(),
    archivedOnly: z.coerce.boolean().optional(),
});
// Re-declared (not `.partial()`) so the `kind` default does NOT fire on a
// PATCH that omits `kind` — partialling a defaulted field would silently
// reset it to "general".
export const WorkspaceUpdateSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    kind: z.enum(WORKSPACE_KINDS).optional(),
    linkedEntityType: z.string().max(40).optional(),
    linkedEntityId: z.string().uuid().optional(),
    visibleToRoles: z.array(z.string().max(40)).max(20).optional(),
});
export const WorkspaceFolderCreateSchema = z.object({
    parentId: z.string().uuid().nullable().optional(),
    name: z.string().min(1).max(200),
    sectionKey: z.string().max(80).nullable().optional(),
    sortOrder: z.number().int().nonnegative().optional(),
});
export const WorkspaceCollaborationSiteCreateSchema = z.object({
    name: z.string().trim().min(1).max(100),
    sitePath: z.string().trim().min(1).max(500),
    isDefault: z.boolean().optional(),
});
export const WorkspaceDocumentCreateSchema = z.object({
    workspaceId: z.string().uuid(),
    folderId: z.string().uuid().nullable().optional(),
    title: z.string().min(1).max(500),
    ownerEmail: z.string().email().nullable().optional(),
    // REQUEST field: strict enum + default is fine (a bad value must be a 400).
    storageLocation: z.enum(WORKSPACE_STORAGE_LOCATIONS).default("blob"),
    collaborationSiteId: z.string().uuid().nullable().optional(),
});
export const WorkspaceDocumentUpdateSchema = z.object({
    title: z.string().min(1).max(500).optional(),
    folderId: z.string().uuid().nullable().optional(),
    ownerEmail: z.string().email().nullable().optional(),
    status: z.enum(WORKSPACE_DOC_STATUSES).optional(),
});
// Step 1 of the 3-step version upload (mirrors VaultUploadTokenSchema).
export const WorkspaceVersionUploadTokenSchema = z.object({
    filename: z.string().min(1).max(500),
    contentType: z.string().min(1).max(100),
});
// Step 3: register the uploaded bytes as a new version. blobKey is the
// SAME server-issued key returned by the upload-token call.
export const WorkspaceVersionCreateSchema = z.object({
    filename: z.string().min(1).max(500),
    mimeType: z.string().min(1).max(100),
    sizeBytes: z
        .number()
        .int()
        .nonnegative()
        .max(200 * 1024 * 1024),
    blobKey: z.string().min(1),
    note: z.string().max(2000).optional(),
});
// Adopt-in-place (ADR-0103): register EXISTING SharePoint drive items as
// working documents where they already live — no copy, no re-upload. Explicit
// itemIds and/or one folder sweep (non-recursive). folderPath "" = drive root,
// which is why the refine checks `!== undefined` rather than truthiness.
export const WorkspaceAdoptDocumentsSchema = z
    .object({
    collaborationSiteId: z.string().uuid().optional(),
    folderId: z.string().uuid().nullable().optional(),
    itemIds: z.array(z.string().min(1).max(200)).max(50).optional(),
    folderPath: z.string().max(1000).optional(),
    /** Sweep subfolders too (ADR-0105). Ignored without `folderPath`. */
    recursive: z.boolean().optional(),
    /** Levels below `folderPath`; clamped server-side to the hard ceiling. */
    maxDepth: z.number().int().min(0).max(10).optional(),
    note: z.string().max(500).optional(),
})
    .refine((v) => (v.itemIds && v.itemIds.length > 0) || v.folderPath !== undefined, {
    message: "Provide itemIds and/or folderPath",
});
// Browse an attached collaboration site's folder (read-only picker source).
export const WorkspaceBrowseSiteQuerySchema = z.object({
    siteId: z.string().uuid().optional(),
    path: z.string().max(1000).optional(),
});
export const WorkspaceLockSchema = z.object({ lock: z.boolean() });
export const PromoteToVaultSchema = z.object({
    vaultFolderId: z.string().uuid(),
    /** When true, Office sources (docx/xlsx/pptx) are rendered to PDF before Vault create. */
    renderAsPdf: z.boolean().optional(),
    /** When true, heuristic NDA signature fields are placed on the promoted PDF (requires PDF bytes). */
    applySignatureFields: z.boolean().optional(),
    /**
     * Optional Vault tags applied on create (e.g. `template:nda`, `topology:bilateral`).
     * Prepare-from-working sets these so Send picks Party A pre-auth without retagging.
     */
    tags: z.array(z.string().min(1).max(100)).max(20).optional(),
});
export const FinaliseWorkingPaperSchema = z.object({
    workspaceDocumentId: z.string().uuid(),
    vaultFolderId: z.string().uuid(),
    sectionKey: z.string().max(80).nullable().optional(),
    includeInPack: z.boolean().optional(),
});
// ─── Response schemas (LOOSE: plain z.string(), no .email()/.uuid() — a single
// legacy/system row must not break a whole list response) ───
export const WorkspaceSchema = z.object({
    id: z.string(),
    name: z.string(),
    kind: z.string(),
    linkedEntityType: z.string().nullable(),
    linkedEntityId: z.string().nullable(),
    visibleToRoles: z.array(z.string()),
    ownerEmail: z.string().nullable(),
    /** Convenience mirror of `ownerEmail !== null` so the UI need not infer it. */
    isPersonal: z.boolean(),
    archivedAt: z.string().nullable(),
    archivedBy: z.string().nullable(),
    createdBy: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const WorkspaceCollaborationSiteSchema = z.object({
    id: z.string(),
    workspaceId: z.string(),
    name: z.string(),
    sitePath: z.string(),
    isDefault: z.boolean(),
    createdBy: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const WorkspaceDocumentSchema = z.object({
    id: z.string(),
    workspaceId: z.string(),
    folderId: z.string().nullable(),
    title: z.string(),
    ownerEmail: z.string().nullable(),
    status: z.string(),
    currentVersionId: z.string().nullable(),
    storageLocation: z.string(),
    collaborationSiteId: z.string().nullable().optional(),
    lockOwnerEmail: z.string().nullable(),
    promotedVaultDocumentId: z.string().nullable(),
    version: z.number().nullable(),
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const PromoteToVaultResponseSchema = z.object({
    vaultDocumentId: z.string(),
    documentId: z.string(),
    renderedAsPdf: z.boolean().optional(),
    templateFieldCount: z.number().int().nonnegative().optional(),
});
// LOOSE responses for the adopt-in-place flow (ADR-0103).
export const WorkspaceBrowseEntrySchema = z.object({
    itemId: z.string(),
    name: z.string(),
    isFolder: z.boolean(),
    sizeBytes: z.number().nullable(),
    lastModified: z.string().nullable(),
    alreadyAdopted: z.boolean(),
});
export const WorkspaceBrowseResponseSchema = z.object({
    siteName: z.string(),
    folderPath: z.string(),
    entries: z.array(WorkspaceBrowseEntrySchema),
});
export const WorkspaceAdoptResponseSchema = z.object({
    siteName: z.string(),
    adopted: z.array(z.object({
        documentId: z.string(),
        title: z.string(),
        filename: z.string(),
        itemId: z.string(),
    })),
    skipped: z.array(z.object({
        itemId: z.string(),
        name: z.string(),
        reason: z.string(),
    })),
    /** Graph listings the sweep made — one per folder opened. */
    foldersVisited: z.number().int().min(0),
});
// ─── Word comments on a working document (ADR-0105) ────────────────────────
export const WorkspaceCommentSchema = z.object({
    id: z.number().int(),
    author: z.string(),
    initials: z.string(),
    date: z.string(),
    text: z.string(),
    resolved: z.boolean(),
    paraId: z.string().nullable(),
});
export const WorkspaceCommentsResponseSchema = z.object({
    documentId: z.string(),
    driveId: z.string(),
    itemId: z.string(),
    comments: z.array(WorkspaceCommentSchema),
});
/** Omitted / empty `commentIds` means every comment on the document. */
export const WorkspaceChangeCommentsSchema = z.object({
    commentIds: z.array(z.number().int().min(0)).max(200).optional(),
    note: z.string().max(500).optional(),
});
export const WorkspaceResolveCommentsSchema = WorkspaceChangeCommentsSchema.extend({
    /** False reopens a resolved comment. */
    resolved: z.boolean().optional(),
});
export const WorkspaceChangeCommentsResponseSchema = z.object({
    documentId: z.string(),
    driveId: z.string(),
    itemId: z.string(),
    changed: z.array(z.number().int()),
    missing: z.array(z.number().int()),
    sizeBytes: z.number().int().min(0),
    versionId: z.string().optional(),
    version: z.number().int().optional(),
});
// LOOSE response: `url` is a signed blob download URL OR a SharePoint webUrl;
// `storageLocation` echoes the document's backend. Plain z.string() — never
// narrow a response field (a single legacy row must not break the response).
export const WorkspaceDocumentEditUrlSchema = z.object({
    url: z.string(),
    storageLocation: z.string(),
});
//# sourceMappingURL=workspaces.js.map